import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Reveal, EASE } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEGMENTS = [
  "Church", "Ministry leader", "Content creator", "Course creator",
  "Small group leader", "Campus ministry", "Just interested",
];

export const Waitlist = () => {
  const [form, setForm] = useState({ name: "", email: "", segment: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.segment) {
      toast.error("Please add your name, email, and who you are.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, form);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="relative py-16 md:py-24 scroll-mt-20">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="relative bg-ink text-sage rounded-[2rem] p-8 md:p-14 overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-green-dark/30 blur-3xl" />

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE }}
                className="relative text-center py-8"
                data-testid="waitlist-success"
              >
                <div className="mx-auto w-16 h-16 rounded-full bg-green flex items-center justify-center mb-8">
                  <Check className="w-8 h-8 text-white" strokeWidth={2.5} />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl mb-4">You're on the list.</h2>
                <p className="text-lg text-sage/75">We'll be in touch soon.</p>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative"
              >
                <Reveal>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-green mb-4">
                    Join The Waitlist
                  </p>
                  <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-3 text-balance">
                    Help Build What The Church Deserves.
                  </h2>
                  <p className="text-sage/70 mb-10 max-w-lg">
                    Tell us who you are and we'll reach out as we open the platform.
                  </p>
                </Reveal>

                <form onSubmit={submit} className="space-y-6" data-testid="waitlist-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="wl-name" className="text-sage/80">Name</Label>
                      <Input
                        id="wl-name"
                        data-testid="waitlist-name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Your name"
                        className="bg-sage/10 border-sage/20 text-sage placeholder:text-sage/60 h-12 rounded-xl focus-visible:ring-green"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wl-email" className="text-sage/80">Email</Label>
                      <Input
                        id="wl-email"
                        type="email"
                        data-testid="waitlist-email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@example.com"
                        className="bg-sage/10 border-sage/20 text-sage placeholder:text-sage/60 h-12 rounded-xl focus-visible:ring-green"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sage/80">I am a…</Label>
                    <Select value={form.segment} onValueChange={(v) => update("segment", v)}>
                      <SelectTrigger
                        data-testid="waitlist-segment"
                        className="bg-sage/10 border-sage/20 text-sage h-12 rounded-xl data-[placeholder]:text-sage/60 focus:ring-green"
                      >
                        <SelectValue placeholder="Select one" />
                      </SelectTrigger>
                      <SelectContent>
                        {SEGMENTS.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`segment-${s.toLowerCase().replace(/\s/g, "-")}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wl-note" className="text-sage/80">Tell us about your community <span className="text-sage/40">(optional)</span></Label>
                    <Textarea
                      id="wl-note"
                      data-testid="waitlist-note"
                      value={form.note}
                      onChange={(e) => update("note", e.target.value)}
                      placeholder="Who are you walking with?"
                      rows={3}
                      className="bg-sage/10 border-sage/20 text-sage placeholder:text-sage/60 rounded-xl focus-visible:ring-green resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="waitlist-submit"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-green px-8 py-4 text-base font-semibold text-white hover:bg-green-dark transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting ? "Adding you…" : "Join the waitlist"}
                    {!submitting && <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />}
                  </button>
                  <p className="text-center text-xs text-sage/50">No spam. No hype. We'll only reach out when it matters.</p>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
